import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { driverConfirm, getBooking } from 'redux/booking/car/carBookingSlice';
import { Card, Badge, Button } from 'components/common';

const Field = ({ label, children }) => (
  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-1 tw-mb-1.5 tw-text-sm">
    <span className="sm:tw-w-1/2 tw-font-semibold tw-text-slate-600 sm:tw-text-right">{label}</span>
    <span className="sm:tw-w-1/2 tw-text-blue-700">{children}</span>
  </div>
);
Field.propTypes = { label: PropTypes.node, children: PropTypes.node };

const TransactionDriverConfirm = () => {
  const { recordData: transaction } = useSelector((state) => state.booking_car);
  const [data, setData] = useState({ type: '', message: '' });
  const params = useParams();
  const dispatch = useDispatch();

  const transactionId = params.id;
  const driverTranId = params.driver_tran_id;
  const { allow_update_departure_time } = transaction?.permissions || {};

  useEffect(() => {
    const handleDriverConfirm = async () => {
      try {
        const response = await dispatch(driverConfirm({ transactionId, driverTranId }));
        const payload = response.payload;
        if (payload.error !== undefined) {
          setData({ type: 'Error', message: payload.error });
        } else {
          setData({ type: 'Success', message: payload.message });
        }
      } catch (error) {
        console.error('Error in driver confirmation:', error);
      }
    };
    handleDriverConfirm();
    // confirm exactly once on mount; this is a mutation, re-running on route-param change would re-submit the confirmation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!transaction) dispatch(getBooking(transactionId));
  }, [transaction, transactionId, dispatch]);

  const isSuccess = data && data.type === 'Success';
  const textColor = isSuccess ? 'tw-text-green-600' : 'tw-text-amber-600';
  const formatDate = (date) => (date ? format(new Date(date), 'dd-MMM-yyyy h:mm aa') : '');

  return (
    <div className="tw-p-4">
      <Card>
        <div className="tw-text-center tw-py-6">
          {isSuccess
            ? <FaCheckCircle className={`${textColor} tw-mx-auto tw-mb-4`} size={64} />
            : <FaExclamationTriangle className={`${textColor} tw-mx-auto tw-mb-4`} size={64} />}

          <h3 className={`${textColor} tw-font-semibold`}>{data.message}</h3>
          {isSuccess && <p className="tw-text-slate-600">You have confirmed car booking.</p>}

          <div className="tw-my-3 tw-border tw-border-solid tw-border-slate-200 tw-rounded tw-p-4 tw-max-w-2xl tw-mx-auto tw-text-left">
            <p className="tw-text-blue-600 tw-mb-3 tw-flex tw-items-center tw-gap-1"><FaInfoCircle /> Booking information:</p>
            <Field label="Departure date:">{formatDate(transaction.from_date)}</Field>
            <Field label="End date:">{formatDate(transaction.to_date)}</Field>
            <Field label="Address pickup location:">{transaction.pickup_location}</Field>
            <Field label="Address drop location:">{transaction.drop_location}</Field>
            <Field label="Name of passengers:">
              <span className="tw-flex tw-flex-wrap tw-gap-1">
                {(transaction.name_of_passengers || []).map((item, i) => <Badge key={i} variant="primary" size="sm">{item}</Badge>)}
              </span>
            </Field>
            <Field label="Route:">{transaction.route}</Field>
            <Field label="Site office:">{transaction.site_name}</Field>
          </div>

          {allow_update_departure_time && (
            <Link to={`/booking/car/${transactionId}/driver-trans/${driverTranId}/update`}>
              <Button size="sm" variant="primary" className="tw-mt-3">Departure start now!</Button>
            </Link>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TransactionDriverConfirm;
